import { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Popover,
} from "@mui/material";
import CustomInput from "global/components/CustomInput/CustomInput";
import filtersStyles from "../SignRequestsFilters.styles";
import signRequestsFiltersTokens from "../SignRequestsFilters.tokens";
import {
  isTagLimitReached,
  SignRequestTag,
} from "../SignRequestsFiltersHelper";
import { InfoCircleGlyph, PlusGlyph } from "./SignRequestsIcons";

const styles = filtersStyles;
const { tags: tagTokens } = signRequestsFiltersTokens;

interface RequestTagPickerProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  tags: SignRequestTag[];
  /** Ids of the tags already on the request the picker was opened from. */
  selected: string[];
  /** The tag list is being read. */
  loading: boolean;
  /** A change to this request's tags is being saved. */
  busy: boolean;
  onToggle: (tagId: string) => void;
  /** Creates a tag from the typed name and puts it on the request. */
  onCreate: (name: string) => void;
  onManageTags: () => void;
}

/**
 * Popover opened from a request row's tag trigger: find a tag, tick it on or
 * off, or create one from whatever was typed.
 */
const RequestTagPicker = ({
  anchorEl,
  onClose,
  tags,
  selected,
  loading,
  busy,
  onToggle,
  onCreate,
  onManageTags,
}: RequestTagPickerProps) => {
  const [query, setQuery] = useState("");
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  const trimmed = query.trim();
  const needle = trimmed.toLowerCase();
  const matches = tags.filter(
    (tag) => !needle || tag.name.toLowerCase().includes(needle),
  );
  const hasExactMatch = tags.some((tag) => tag.name.toLowerCase() === needle);
  const atLimit = isTagLimitReached(selected);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      PaperProps={{
        sx: styles.pickerPaper,
        role: "dialog",
        "aria-label": "Tags for this request",
      }}
      // A click inside the picker must not open the row underneath it.
      onClick={(event) => event.stopPropagation()}
    >
      {/* Always mounted so the popover does not jump when a save starts. */}
      <LinearProgress
        aria-label={busy ? "Saving tags" : "Loading tags"}
        sx={styles.pickerProgress(busy || loading)}
      />
      <Box sx={styles.pickerSearch}>
        <CustomInput
          autoFocus
          name="tagSearch"
          placeHolder="Find or create a tag"
          value={query}
          propsToInputElement={{ "aria-label": "Find or create a tag" }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setQuery(event.target.value)
          }
        />
      </Box>

      {tags.length === 0 && !trimmed && !loading && (
        <Box sx={styles.pickerHint}>
          Tags help you group requests. Create your first tag.
        </Box>
      )}

      <Box sx={styles.pickerList}>
        {matches.map((tag) => {
          const checked = selected.includes(tag.id);
          return (
            <FormControlLabel
              key={tag.id}
              sx={styles.pickerItem}
              // Past the limit a request can still shed tags, just not gain any.
              disabled={busy || (atLimit && !checked)}
              control={
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={() => onToggle(tag.id)}
                />
              }
              label={
                <>
                  <Box component="span" sx={styles.tagDot(tag.color)} />
                  {tag.name}
                </>
              }
            />
          );
        })}
      </Box>

      {Boolean(trimmed) && !hasExactMatch && (
        <ButtonBase
          sx={styles.pickerCreate}
          disabled={busy}
          onClick={() => {
            onCreate(trimmed);
            setQuery("");
          }}
        >
          <PlusGlyph sx={styles.pickerCreateGlyph} />
          Create tag "{trimmed}"
        </ButtonBase>
      )}

      {atLimit && (
        <Box sx={styles.pickerLimit}>
          <InfoCircleGlyph sx={styles.pickerLimitGlyph} />
          Max {tagTokens.limit} tags per request
        </Box>
      )}

      <Box sx={styles.pickerFooter}>
        <Box component="span" sx={styles.pickerCount}>
          {selected.length} of {tagTokens.limit} tags
        </Box>
        <Button
          variant="text"
          disableRipple
          onClick={onManageTags}
          sx={styles.manageLink}
        >
          Manage tags
        </Button>
      </Box>
    </Popover>
  );
};

export default RequestTagPicker;
